import SlideFrame from "@/components/SlideFrame";
import { useState, useRef } from "react";
import {
  Building2, ShieldCheck, GraduationCap, Sparkles, Upload, Send, Check,
  AlertTriangle, FileText, ArrowRight, Loader2, MessageSquareText, Award,
  PhoneCall, Briefcase, Brain, Timer, User, MessageSquare, Mail, Calendar,
  ChevronRight, Eye, Clock, Bell, ArrowDown, Network, Activity, TrendingUp,
  Download, ExternalLink, Users, ShieldAlert, Radio,
} from "lucide-react";
import { toast } from "sonner";
import { isLegalPrimary, packFor } from "@/lib/industryPack";

/* ============================ 07 · Try It Yourself ============================ */
export function S_TryIt({ idx, total, prospect }) {
  const pack = packFor(prospect);
  const ALL_MODULES = {
    vendor:    { id: "vendor",    icon: Building2,     label: "Vendor onboarding",  sub: "Third-party risk" },
    marketing: { id: "marketing", icon: ShieldCheck,   label: "Marketing review",   sub: "AI-assisted review" },
    training:  { id: "training",  icon: GraduationCap, label: "Training quiz",      sub: "Video + multiple choice" },
    hotline:   { id: "hotline",   icon: PhoneCall,     label: "Hotline intake",     sub: "Anonymous + triage" },
    matter:    { id: "matter",    icon: Briefcase,     label: "Matter status tracker", sub: "Live status · text counsel" },
    "complimind-gc": { id: "complimind-gc", icon: Brain, label: "CompliMind for GCs", sub: "Ask anything · we surface receipts" },
    hours:     { id: "hours",     icon: Timer,         label: "Hour tracker · client markers", sub: "Stop-and-notify · budget guardrails" },
    "partner-monitoring": { id: "partner-monitoring", icon: Network, label: "Partner monitoring view", sub: "Cross-tenant oversight · live signals" },
  };
  const labelOverrides = pack.tryItLabels || {};
  const modules = (pack.tryItModules || ["vendor", "marketing", "training"])
    .map((id) => ALL_MODULES[id])
    .filter(Boolean)
    .map((m) => labelOverrides[m.id] ? { ...m, ...labelOverrides[m.id] } : m);
  const [active, setActive] = useState(modules[0]?.id || "vendor");

  return (
    <SlideFrame idx={idx} total={total} kicker="Try It Yourself" dark>
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="max-w-3xl">
          <p className="mono text-[11px] uppercase tracking-[0.22em] text-sun-700 mb-3">/ click around · these are real workflows</p>
          <h2 className="serif font-light text-4xl md:text-5xl leading-[1.05] tracking-tight">
            Play around with the {modules.length} interfaces below to see how easy compliance{" "}
            <em className="italic text-sun-600">can be — easy, intuitive, and built for the way you actually work.</em>
          </h2>
          <p className="mt-4 text-ink-900/80 text-base md:text-lg leading-relaxed max-w-2xl">
            Click into any one of them right here. When you want more, sign in for free at{" "}
            <a
              href="https://complisunguardian.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="guardian-link"
              className="italic text-sun-700 underline decoration-sun-500/40 underline-offset-4 hover:text-sun-700 hover:decoration-sun-500"
            >
              complisunguardian.com
            </a>{" "}
            and play with the full set of our free tools too.
          </p>
          <p className="mt-3 text-ink-900/70 text-sm leading-relaxed max-w-2xl" data-testid="tryit-guardian-clarifier">
            <strong className="text-ink-900">A note on the free playground:</strong>{" "}
            complisunguardian.com is a free version of the platform so you can play with the AI
            and the automation yourself. Your <em className="italic">enterprise</em> environment
            is a different beast — stronger, fully protected, and the prototype we build for you
            will be unique to your business. If you see a tool here that you love, we'll lift the
            concept into your build; we won't ship you this playground.
          </p>
          {pack.tryItSubhead && (
            <p className="mt-2 text-ink-900/65 text-sm leading-relaxed max-w-2xl italic" data-testid="tryit-subhead">
              {pack.tryItSubhead}
            </p>
          )}
        </div>

        {/* Floating "fake document tray" — top right (only for modules that use drag/drop) */}
        {modules.some((m) => ["vendor", "marketing"].includes(m.id)) && (
        <div className="border border-ink-900/12 rounded-md bg-white/75 px-3 py-2.5 mt-2" data-testid="sample-docs-tray">
          <div className="mono text-[9px] uppercase tracking-[0.22em] text-sun-700 mb-1.5">/ sample documents · drag into the demo</div>
          <div className="flex gap-2 flex-wrap">
            <DocChip name="Acme_KYC.pdf" />
            <DocChip name="rate_promo.html" />
            <DocChip name="employee_policy.pdf" />
          </div>
        </div>
        )}
      </div>

      <div className={`mt-7 grid grid-cols-1 ${modules.length >= 4 ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3"} gap-3`} data-testid="tryit-tabs">
        {modules.map((m) => {
          const Icon = m.icon;
          const on = m.id === active;
          return (
            <button key={m.id} onClick={() => setActive(m.id)} data-testid={`tryit-tab-${m.id}`}
              className={`text-left rounded-md border px-4 py-3 transition-colors flex items-start gap-3
                ${on ? "border-sun-500 bg-sun-500/10" : "border-ink-900/12 bg-white/60 hover:border-ink-900/25"}`}>
              <Icon className={`h-5 w-5 mt-0.5 ${on ? "text-sun-700" : "text-ink-900/55"}`} strokeWidth={1.5} />
              <div>
                <div className="serif text-base text-ink-900 leading-tight">{m.label}</div>
                <div className="mono text-[9px] uppercase tracking-[0.22em] text-ink-900/55 mt-0.5">{m.sub}</div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-5">
        {active === "vendor"        && <VendorDemo />}
        {active === "marketing"     && <MarketingDemo />}
        {active === "training"      && <TrainingDemo />}
        {active === "hotline"       && <HotlineDemo />}
        {active === "matter"        && <MatterTrackerDemo prospect={prospect} />}
        {active === "complimind-gc" && <CompliMindGCDemo prospect={prospect} />}
        {active === "hours"         && <HourTrackerDemo prospect={prospect} />}
        {active === "partner-monitoring" && <PartnerMonitoringDemo prospect={prospect} />}
      </div>
    </SlideFrame>
  );
}

function DocChip({ name }) {
  const onDragStart = (e) => {
    e.dataTransfer.setData("text/plain", `sample:${name}`);
    e.dataTransfer.effectAllowed = "copy";
  };
  return (
    <span
      draggable
      onDragStart={onDragStart}
      data-testid={`sample-doc-${name}`}
      className="inline-flex items-center gap-1.5 cursor-grab active:cursor-grabbing mono text-[10px] uppercase tracking-[0.18em] border border-ink-900/15 rounded px-2 py-1 bg-cream-100 hover:border-sun-500 hover:text-sun-700 transition-colors"
    >
      <FileText className="h-3 w-3" /> {name}
    </span>
  );
}


function dropzoneCls(drop) {
  if (drop === "dragging") return "border-sun-500 bg-sun-500/15 scale-[1.01]";
  if (drop === "parsing") return "border-sun-500/60 bg-white/80";
  if (drop === "done") return "border-emerald-500/40 bg-emerald-500/5";
  return "border-ink-900/15 bg-white/55 hover:border-sun-500/60 hover:bg-white/75";
}

/* ===================== Vendor demo ===================== */
export function VendorDemo() {
  const STEPS = [
    "Parsing OCR + key fields…",
    "Resolving entity registration · DE Division of Corporations…",
    "Running OFAC + UN consolidated · principals matched: 0",
    "PEP screening · principals matched: 0",
    "Adverse-news scan · 1 minor result on CFO",
    "Tier scoring + recommendation drafted",
  ];
  const [drop, setDrop] = useState("idle"); // idle | dragging | parsing | done
  const [parsed, setParsed] = useState([]);

  const run = () => {
    setDrop("parsing"); setParsed([]);
    STEPS.forEach((s, i) => setTimeout(() => setParsed((p) => [...p, s]), 250 + i * 360));
    setTimeout(() => setDrop("done"), 250 + STEPS.length * 360 + 100);
  };

  return (
    <div className="grid grid-cols-12 gap-4" data-testid="vendor-demo">
      <div className="col-span-12 md:col-span-7">
        <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-1">
          / step 1 · drop a vendor packet from the tray above (or click)
        </div>
        <div
          onDragOver={(e) => { e.preventDefault(); setDrop("dragging"); }}
          onDragLeave={() => setDrop("idle")}
          onDrop={(e) => { e.preventDefault(); run(); }}
          onClick={() => drop === "idle" && run()}
          data-testid="vendor-dropzone"
          className={`cursor-pointer rounded-md border-2 border-dashed p-4 transition-all ${dropzoneCls(drop)}`}
        >
          <div className="flex items-start gap-3">
            <div className={`h-12 w-12 rounded-md grid place-items-center flex-shrink-0
              ${drop === "parsing" ? "bg-sun-500/15" : drop === "done" ? "bg-emerald-500/15" : "bg-cream-200"}`}>
              {drop === "parsing" ? <Loader2 className="h-5 w-5 text-sun-700 animate-spin" />
                : drop === "done" ? <Check className="h-5 w-5 text-emerald-700" />
                : <FileText className="h-5 w-5 text-ink-900/55" strokeWidth={1.5} />}
            </div>
            <div className="flex-1">
              <div className="serif text-base text-ink-900">
                {drop === "parsing" ? "Acme_KYC.pdf — AI is reading…"
                  : drop === "done" ? "Acme_KYC.pdf — risk pack ready"
                  : "Drag a vendor packet here"}
              </div>
              {parsed.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {parsed.map((s) => (
                    <li key={s} className="flex items-center gap-2 text-xs text-ink-900/75 animate-fade-in">
                      <Check className="h-3 w-3 text-emerald-700 flex-shrink-0" />{s}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="col-span-12 md:col-span-5">
        <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-1">/ step 2 · review · send onboarding form</div>
        {drop === "done" ? (
          <div className="border border-ink-900/10 rounded-md p-4 bg-white/80 space-y-3 animate-fade-in" data-testid="vendor-result">
            <div className="flex items-center justify-between">
              <div className="serif text-base text-ink-900">Acme Payouts LLC</div>
              <div className="mono text-[10px] uppercase tracking-[0.22em] border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 rounded-full px-2.5 py-1">
                Tier 1 · Low risk
              </div>
            </div>
            <div className="text-xs text-ink-900/70 leading-relaxed">
              Principals ≥25% own: Marcus Hale (38%), Priya Subramanian (26%). OFAC clear,
              PEP clear, one minor adverse-news hit on CFO (non-material). SOC 2 Type II current.
            </div>
            <button className="w-full mono text-[10px] uppercase tracking-[0.22em] text-cream bg-ink-900 hover:bg-ink-800 rounded px-3 py-2 inline-flex items-center justify-center gap-1.5"
                    data-testid="vendor-send">
              <Send className="h-3 w-3" /> Send onboarding form to vendor
            </button>
          </div>
        ) : (
          <div className="border border-dashed border-ink-900/15 rounded-md p-4 bg-white/40 text-sm text-ink-900/55 text-center" style={{ minHeight: 180 }}>
            Drop a vendor packet on the left to populate this panel.
          </div>
        )}
      </div>
    </div>
  );
}

/* ===================== Marketing demo ===================== */
export function MarketingDemo() {
  const SAMPLES = [
    { id: "rate-promo", body: "Get a 4.99% APR* on your loan — guaranteed approval, fast funding, no hidden fees." },
    { id: "ig-ad",      body: "Drop your bank. Switch to FyreBank — we'll cover up to $300 in transfer fees. Limited time. No credit check." },
  ];
  const [text, setText] = useState(SAMPLES[0].body);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [hover, setHover] = useState(false);

  const review = () => {
    setRunning(true); setResult(null);
    setTimeout(() => {
      const lower = text.toLowerCase();
      const findings = [];
      if (lower.includes("guaranteed approval") || lower.includes("no credit check"))
        findings.push({ rule: "FTC Act §5 · Deceptive", sev: "high", msg: "'Guaranteed approval / no credit check' is materially misleading absent disclosed underwriting criteria." });
      if (lower.includes("apr"))
        findings.push({ rule: "Regulation Z · TILA", sev: "high", msg: "APR triggers require disclosure of finance charge, payment schedule, and amount financed in close proximity." });
      if (lower.includes("limited time") || lower.includes("drop your bank"))
        findings.push({ rule: "UDAAP · Abusive", sev: "med", msg: "Urgency + competitor denigration can be challenged under CFPB Bulletin 2022-09." });
      if (lower.includes("hidden fees"))
        findings.push({ rule: "FTC Substantiation", sev: "med", msg: "'No hidden fees' is a substantive claim requiring evidence; flag for substantiation file." });
      setResult({ ok: findings.length === 0, findings });
      setRunning(false);
    }, 800);
  };

  return (
    <div className="grid grid-cols-12 gap-4" data-testid="marketing-demo">
      <div className="col-span-12 md:col-span-5">
        <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-1">
          / step 1 · drop "rate_promo.html" from the tray (or paste your own)
        </div>
        <div
          onDragOver={(e) => { e.preventDefault(); setHover(true); }}
          onDragLeave={() => setHover(false)}
          onDrop={(e) => {
            e.preventDefault();
            setHover(false);
            setText(SAMPLES[0].body); setResult(null);
            toast.success("Detected · landing-page copy parsed");
          }}
          className={`rounded-md border-2 border-dashed transition-all p-2 mb-2 text-[11px] flex items-center gap-2
            ${hover
              ? "border-sun-500 bg-sun-500/15 text-ink-900 scale-[1.01] shadow-sm"
              : "border-ink-900/15 bg-white/55 hover:border-sun-500/60 text-ink-900/55"}`}
          data-testid="marketing-dropzone"
        >
          <Upload className="h-3.5 w-3.5" /> Drag a sample doc here · or paste copy below
        </div>
        <textarea value={text} onChange={(e) => { setText(e.target.value); setResult(null); }}
                  data-testid="marketing-textarea"
                  className="w-full h-32 border border-ink-900/12 rounded-md p-3 text-sm bg-white text-ink-900 focus:outline-none focus:border-sun-500 resize-none" />
        <button onClick={review} disabled={running || !text.trim()}
                data-testid="marketing-run"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-ink-900 hover:bg-ink-800 disabled:opacity-40 text-cream rounded-md px-4 py-2 text-sm transition-colors">
          {running ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Running AI review…</>
                   : <><Sparkles className="h-3.5 w-3.5" /> Step 2 · Run AI compliance review</>}
        </button>
      </div>

      <div className="col-span-12 md:col-span-7 border border-ink-900/10 rounded-md p-4 bg-white/70 min-h-[280px]" data-testid="marketing-results">
        <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700 mb-2">/ step 3 · review AI findings · send to expert</div>
        {!result && !running && (
          <div className="h-full grid place-items-center text-center text-ink-900/45 text-sm">
            <div><FileText className="h-6 w-6 mx-auto mb-2 opacity-50" />Run the review to populate this panel.</div>
          </div>
        )}
        {running && (
          <div className="h-full grid place-items-center text-ink-900/55">
            <div className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Scanning FTC, TILA, UDAAP, Reg-Z…</div>
          </div>
        )}
        {result && (
          <div>
            <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 mono text-[10px] uppercase tracking-[0.22em] border
              ${result.ok ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700" : "border-amber-500/40 bg-amber-500/15 text-amber-700"}`}>
              {result.ok ? <Check className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
              {result.ok ? "Clean — ready to ship" : `${result.findings.length} finding${result.findings.length === 1 ? "" : "s"}`}
            </div>
            <ul className="mt-3 space-y-2">
              {result.findings.map((f) => (
                <li key={f.rule} className="border border-ink-900/8 rounded-md p-3 bg-white">
                  <div className="flex items-center justify-between">
                    <div className="mono text-[9px] uppercase tracking-[0.22em] text-sun-700">{f.rule}</div>
                    <div className={`mono text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 rounded
                      ${f.sev === "high" ? "bg-rose-500/15 text-rose-700" : "bg-amber-500/15 text-amber-700"}`}>{f.sev}</div>
                  </div>
                  <div className="text-sm text-ink-900/80 mt-1.5">{f.msg}</div>
                </li>
              ))}
            </ul>
            <button data-testid="marketing-escalate"
                    className="mt-3 inline-flex items-center gap-2 mono text-[10px] uppercase tracking-[0.22em] text-ink-900 border border-ink-900/15 rounded px-3 py-2 hover:border-sun-500 hover:text-sun-700">
              <ArrowRight className="h-3 w-3" /> Send to a human reviewer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


function quizHeaderLabel(isQuiz, step, total) {
  if (isQuiz) return `step 2 · question ${step + 1} / ${total}`;
  if (step === "attest") return "step 3 · policy attestation";
  return "completed";
}

function choiceCls(isCorrectShown, isWrongChosen, chosen) {
  if (isCorrectShown) return "border-emerald-500/50 bg-emerald-500/10";
  if (isWrongChosen) return "border-rose-500/50 bg-rose-500/10";
  if (chosen) return "border-sun-500 bg-sun-500/10";
  return "border-ink-900/12 bg-white/55 hover:border-ink-900/25";
}

/* ===================== Training demo — video + multiple-choice quiz + attestation ===================== */
export function TrainingDemo() {
  const QUIZ = [
    {
      q: "What's the most common AML red flag at customer onboarding?",
      choices: [
        "A customer with a corporate email address",
        "Unexplained ownership + source-of-funds mismatch",
        "A customer who pays on time",
        "A customer who uses ACH instead of wire",
      ],
      correct: 1,
      explain: "Unexplained ownership structures + source-of-funds mismatches are textbook BSA red flags. The pattern: transaction velocity inconsistent with stated income.",
    },
    {
      q: "When does your team need to file a SAR?",
      choices: [
        "Only when a customer is convicted of a crime",
        "Whenever total transactions exceed $10,000",
        "Within 30 days when you know, suspect, or have reason to suspect illegal activity ≥ $5,000",
        "Once a quarter, in batch",
      ],
      correct: 2,
      explain: "30 days from initial detection of facts triggering the report. Aggregates ≥ $5,000 (≥$2,000 for MSBs). Continuing activity → follow-up SAR every 90 days.",
    },
    {
      q: "UDAAP includes which of the following?",
      choices: [
        "Unfair, Deceptive, AND Abusive — all three required",
        "Unfair OR Deceptive OR Abusive — any one is enough",
        "Only unfairness as defined by FTC",
        "Only acts that mention APR",
      ],
      correct: 1,
      explain: "UDAAP = Unfair, Deceptive, OR Abusive Acts or Practices. Any one prong is sufficient for an enforcement action.",
    },
  ];

  const [step, setStep] = useState(0);            // 0..QUIZ.length-1, then "attest", then "done"
  const [answers, setAnswers] = useState({});      // step -> chosen index
  const [showExplain, setShowExplain] = useState(false);
  const [attested, setAttested] = useState(false);

  const isQuiz = typeof step === "number";
  const cur = isQuiz ? QUIZ[step] : null;
  const correctCount = QUIZ.reduce((n, q, i) => n + (answers[i] === q.correct ? 1 : 0), 0);

  const pick = (i) => {
    setAnswers((a) => ({ ...a, [step]: i }));
    setShowExplain(true);
  };
  const next = () => {
    setShowExplain(false);
    if (step + 1 < QUIZ.length) setStep(step + 1);
    else setStep("attest");
  };

  return (
    <div className="grid grid-cols-12 gap-4" data-testid="training-demo">
      <div className="col-span-12 md:col-span-5">
        <div className="border border-ink-900/10 rounded-md overflow-hidden bg-white/85">
          <div className="aspect-video bg-ink-900">
            <video
              src="https://customer-assets.emergentagent.com/job_complisun-pitch/artifacts/zt6kr5is_Intro%20to%20Data%20Protection.mp4"
              controls preload="metadata"
              className="w-full h-full object-cover"
              data-testid="training-video"
            />
          </div>
          <div className="p-4">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700">/ module 01 · video</div>
            <div className="serif text-base text-ink-900 leading-tight mt-1">Intro to Data Protection</div>
            <div className="text-xs text-ink-900/60 mt-1">Step 1 of 3 · auto-tracked completion</div>
          </div>
        </div>
      </div>

      <div className="col-span-12 md:col-span-7 border border-ink-900/10 rounded-md bg-white/75 flex flex-col" data-testid="training-quiz">
        {/* Header */}
        <div className="px-4 py-3 border-b border-ink-900/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquareText className="h-4 w-4 text-sun-700" strokeWidth={1.5} />
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/70">
              {quizHeaderLabel(isQuiz, step, QUIZ.length)}
            </div>
          </div>
          {isQuiz && (
            <div className="flex gap-1">
              {QUIZ.map((q, i) => (
                <div key={q.q} className={`h-1.5 w-6 rounded-full ${i === step ? "bg-sun-500" : i < step ? "bg-emerald-500" : "bg-ink-900/15"}`} />
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 p-5">
          {isQuiz && cur && (
            <div>
              <div className="serif text-lg text-ink-900 leading-snug">{cur.q}</div>
              <div className="mt-4 space-y-2">
                {cur.choices.map((c, i) => {
                  const chosen = answers[step] === i;
                  const correct = cur.correct === i;
                  const isWrongChosen = showExplain && chosen && !correct;
                  const isCorrectShown = showExplain && correct;
                  return (
                    <button
                      key={c}
                      onClick={() => !showExplain && pick(i)}
                      disabled={showExplain}
                      data-testid={`training-quiz-${step}-choice-${i}`}
                      className={`w-full text-left rounded-md border px-3 py-2.5 text-sm transition-colors flex items-start gap-2.5 ${choiceCls(isCorrectShown, isWrongChosen, chosen)}`}
                    >
                      <span className={`mono text-[10px] uppercase tracking-[0.22em] mt-0.5
                        ${isCorrectShown ? "text-emerald-700" : isWrongChosen ? "text-rose-700" : "text-ink-900/45"}`}>
                        {String.fromCharCode(65 + i)}
                      </span>
                      <span className="text-ink-900/85">{c}</span>
                      {isCorrectShown && <Check className="h-4 w-4 text-emerald-700 ml-auto" />}
                      {isWrongChosen && <AlertTriangle className="h-4 w-4 text-rose-700 ml-auto" />}
                    </button>
                  );
                })}
              </div>
              {showExplain && (
                <div className="mt-3 border-l-2 border-sun-500 pl-3 bg-cream-100 p-3 rounded-r animate-fade-in">
                  <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700">/ why</div>
                  <p className="text-sm text-ink-900/80 mt-1">{cur.explain}</p>
                </div>
              )}
              {showExplain && (
                <button onClick={next}
                        data-testid="training-quiz-next"
                        className="mt-4 inline-flex items-center gap-2 bg-ink-900 hover:bg-ink-800 text-cream rounded-md px-4 py-2 text-sm">
                  {step + 1 < QUIZ.length ? "Next question" : "Continue to attestation"} <ArrowRight className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}

          {step === "attest" && !attested && (
            <div data-testid="training-attest" className="animate-fade-in">
              <div className="serif text-lg text-ink-900 leading-snug mb-1">Acknowledge the AML / BSA Policy</div>
              <div className="mono text-[9px] uppercase tracking-[0.22em] text-ink-900/55">Quiz score: {correctCount} of {QUIZ.length}</div>
              <div className="mt-4 border border-ink-900/10 rounded-md p-3 bg-cream-100 text-xs text-ink-900/75 leading-relaxed max-h-32 overflow-y-auto">
                <strong>AML / BSA Policy · v3.2</strong> — I acknowledge that I have completed the
                Intro to Data Protection training, understand my role in identifying and reporting
                suspicious activity, and will follow the SAR filing protocol described in §4.3. I
                understand that failing to escalate red flags exposes the company to enforcement risk
                and creates personal liability under 31 U.S.C. § 5318.
              </div>
              <button onClick={() => setAttested(true)}
                      data-testid="training-attest-submit"
                      className="mt-4 inline-flex items-center gap-2 bg-ink-900 hover:bg-ink-800 text-cream rounded-md px-4 py-2 text-sm">
                <Check className="h-3.5 w-3.5" /> I acknowledge and accept this policy
              </button>
            </div>
          )}

          {attested && (
            <div data-testid="training-done" className="text-center py-6 animate-fade-in">
              <div className="inline-flex h-14 w-14 rounded-full bg-sun-500/15 border border-sun-500/40 grid place-items-center mb-3">
                <Award className="h-6 w-6 text-sun-700" strokeWidth={1.5} />
              </div>
              <div className="serif text-2xl text-ink-900">Module complete.</div>
              <div className="text-sm text-ink-900/65 mt-1.5">
                {correctCount} / {QUIZ.length} correct · attestation logged · audit-ready evidence captured.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ===================== Hotline demo (for law/healthcare) ===================== */
function HotlineDemo() {
  const [submitted, setSubmitted] = useState(false);
  return (
    <div className="grid grid-cols-12 gap-4" data-testid="hotline-demo">
      <div className="col-span-12 md:col-span-7 border border-ink-900/10 rounded-md p-4 bg-white/70">
        <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700 mb-2">/ anonymous report intake</div>
        {!submitted ? (
          <form onSubmit={(e) => { e.preventDefault(); setSubmitted(true); }}>
            <label className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55">Concern type</label>
            <select className="w-full h-10 border border-ink-900/12 rounded-md px-2 mt-1 bg-white text-sm">
              <option>Conflict of interest</option><option>Harassment / discrimination</option>
              <option>Financial misconduct</option><option>Other</option>
            </select>
            <label className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mt-3 block">Describe what happened</label>
            <textarea defaultValue="Demo — a colleague mentioned that the new vendor selection skipped the conflict-of-interest review."
                      className="w-full h-24 border border-ink-900/12 rounded-md p-2 mt-1 bg-white text-sm resize-none" />
            <button className="mt-3 bg-ink-900 hover:bg-ink-800 text-cream rounded-md px-4 py-2 text-sm" type="submit">Submit anonymously</button>
          </form>
        ) : (
          <div className="animate-fade-in">
            <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 mono text-[10px] uppercase tracking-[0.22em] border border-emerald-500/30 bg-emerald-500/10 text-emerald-700">
              <Check className="h-3 w-3" /> Case #HL-2026-014 created
            </div>
            <p className="text-sm text-ink-900/80 mt-3">Triage assigned to outside counsel. Reporter receives an anonymous reference code; investigator gets the case file; both with full audit trail.</p>
          </div>
        )}
      </div>
      <div className="col-span-12 md:col-span-5 border border-ink-900/10 rounded-md p-4 bg-white/70">
        <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700 mb-2">/ revenue line for your firm</div>
        <p className="text-sm text-ink-900/80 leading-relaxed">
          Your firm white-labels this hotline + intake + triage workflow for YOUR clients. They get
          a hotline they can advertise to their employees. You get a new service line that's
          billable, scalable, and stickier than retainer hours.
        </p>
      </div>
    </div>
  );
}


/* ===================== Matter Status Tracker (GC view) ===================== */
function MatterTrackerDemo({ prospect }) {
  const slug = (prospect?.slug || "").toLowerCase();
  const isSloan = slug === "sloan";

  const SLOAN_MATTERS = [
    {
      id: "m-2031",
      name: "Distributor MSA Renewal — Ferguson Enterprises",
      stage: 2,
      attorney: "Jennifer Green",
      docs: [
        { stage: 0, title: "Memo Assignment — MSA renewal scope",                from: "GC", date: "Feb 6" },
        { stage: 1, title: "Research notes · pricing tiers + rebate carve-outs", from: "Jennifer Green", date: "Feb 9" },
        { stage: 2, title: "Draft v1 — MSA renewal redlines",                    from: "Jennifer Green", date: "Feb 14" },
      ],
    },
    {
      id: "m-2032",
      name: "Patent Enforcement — Sloan 8186 touchless flushometer",
      stage: 4,
      attorney: "Li-Chen Hung · outside IP counsel",
      docs: [
        { stage: 0, title: "Memo Assignment — infringement assessment",          from: "GC", date: "Feb 3" },
        { stage: 2, title: "Claim chart v1 · accused product mapping",           from: "Li-Chen Hung", date: "Feb 6" },
        { stage: 3, title: "Internal review · cease-and-desist strategy (Colin)",from: "Colin Darke",   date: "Feb 8" },
        { stage: 4, title: "C&D letter sent to importer · awaiting response",    from: "Li-Chen Hung",  date: "Feb 10" },
      ],
    },
    {
      id: "m-2033",
      name: "Prop 65 / NSF 372 lead-free cert — 2026 line refresh",
      stage: 1,
      attorney: "Jennifer Green",
      docs: [
        { stage: 0, title: "Memo Assignment — line-refresh certification scope", from: "GC", date: "Feb 12" },
        { stage: 1, title: "Research notes · NSF/ANSI 372 boundaries + warning labels", from: "Jennifer Green", date: "Feb 13" },
      ],
    },
  ];

  const DEFAULT_MATTERS = [
    {
      id: "m-2031",
      name: "2026 Executive Incentive Plan — Annual Refresh",
      stage: 2,
      attorney: "Jennifer Green",
      docs: [
        { stage: 0, title: "Memo Assignment — kickoff brief", from: "GC", date: "Feb 6" },
        { stage: 1, title: "Research notes · § 409A + 162(m)", from: "Jennifer Green", date: "Feb 9" },
        { stage: 2, title: "Draft v1 — Incentive Plan", from: "Jennifer Green", date: "Feb 14" },
      ],
    },
    {
      id: "m-2032",
      name: "MSA Redline — Acme Logistics",
      stage: 4,
      attorney: "Li-Chen Hung",
      docs: [
        { stage: 0, title: "Memo Assignment — MSA review", from: "GC", date: "Feb 3" },
        { stage: 2, title: "Draft v1 — redlines", from: "Li-Chen Hung", date: "Feb 6" },
        { stage: 3, title: "Internal Review notes (Colin)", from: "Colin Darke", date: "Feb 8" },
        { stage: 4, title: "Sent to Acme · awaiting countersign", from: "Li-Chen Hung", date: "Feb 10" },
      ],
    },
    {
      id: "m-2033",
      name: "Vendor DPA — Snowflake renewal",
      stage: 1,
      attorney: "Jennifer Green",
      docs: [
        { stage: 0, title: "Memo Assignment — DPA review", from: "GC", date: "Feb 12" },
        { stage: 1, title: "Research notes · cross-border transfers", from: "Jennifer Green", date: "Feb 13" },
      ],
    },
  ];

  const MATTERS = isSloan ? SLOAN_MATTERS : DEFAULT_MATTERS;
  const STAGES = [
    { key: "received",  label: "Memo received" },
    { key: "working",   label: "Team working" },
    { key: "drafted",   label: "Initial draft" },
    { key: "reviewed",  label: "Internal review" },
    { key: "sent",      label: "Sent · awaiting client" },
  ];
  const [activeId, setActiveId] = useState(MATTERS[0].id);
  const [activeDoc, setActiveDoc] = useState(MATTERS[0].docs.at(-1));
  const [text, setText] = useState("");
  const [sent, setSent] = useState(false);
  const matter = MATTERS.find((m) => m.id === activeId);

  const pickMatter = (id) => {
    setActiveId(id);
    const m = MATTERS.find((mm) => mm.id === id);
    setActiveDoc(m.docs.at(-1));
    setSent(false);
  };

  const send = () => {
    if (!text.trim()) return;
    setSent(true);
    toast.success(`Text to ${matter.attorney} delivered. Reply ETA · 12 min.`);
    setTimeout(() => { setText(""); setSent(false); }, 2200);
  };

  return (
    <div className="grid grid-cols-12 gap-4" data-testid="matter-demo">
      {/* Left: list of matters */}
      <div className="col-span-12 md:col-span-4">
        <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-2">/ your open matters</div>
        <ul className="space-y-2">
          {MATTERS.map((m) => {
            const on = m.id === activeId;
            return (
              <li key={m.id}>
                <button
                  onClick={() => pickMatter(m.id)}
                  data-testid={`matter-${m.id}`}
                  className={`w-full text-left rounded-md border px-3 py-2.5 transition-colors
                    ${on ? "border-sun-500 bg-sun-500/10" : "border-ink-900/12 bg-white/55 hover:border-ink-900/25"}`}
                >
                  <div className="mono text-[9px] uppercase tracking-[0.20em] text-ink-900/55">{m.id} · {m.attorney}</div>
                  <div className="serif text-sm text-ink-900 leading-tight mt-1">{m.name}</div>
                  <div className="mt-2 mono text-[9px] uppercase tracking-[0.18em] text-sun-700">{STAGES[m.stage].label}</div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Right: timeline + doc viewer + quick text */}
      <div className="col-span-12 md:col-span-8 border border-ink-900/10 rounded-md bg-white/75 p-4">
        <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700">/ matter · {matter.id}</div>
        <div className="serif text-xl text-ink-900 leading-tight mt-1">{matter.name}</div>

        {/* Stage strip */}
        <ol className="mt-4 grid grid-cols-5 gap-1" data-testid="matter-stages">
          {STAGES.map((s, i) => {
            const reached = i <= matter.stage;
            return (
              <li key={s.key} className="flex flex-col items-center text-center">
                <div className={`h-6 w-6 grid place-items-center rounded-full text-[10px] mono
                  ${reached ? "bg-sun-500 text-ink-900" : "bg-ink-900/8 text-ink-900/45"}`}>
                  {reached ? <Check className="h-3 w-3" /> : i + 1}
                </div>
                <div className={`mono text-[8px] uppercase tracking-[0.16em] mt-1 leading-tight ${reached ? "text-ink-900" : "text-ink-900/40"}`}>
                  {s.label}
                </div>
                {i < STAGES.length - 1 && (
                  <div className={`hidden md:block absolute h-px ${reached ? "bg-sun-500/50" : "bg-ink-900/10"}`} style={{ width: 0 }} />
                )}
              </li>
            );
          })}
        </ol>

        {/* Doc list + viewer */}
        <div className="mt-4 grid grid-cols-12 gap-3">
          <div className="col-span-12 md:col-span-5">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-1.5">/ docs at each stage</div>
            <ul className="space-y-1.5">
              {matter.docs.map((d, i) => {
                const on = activeDoc?.title === d.title;
                return (
                  <li key={d.title}>
                    <button
                      onClick={() => setActiveDoc(d)}
                      data-testid={`matter-doc-${i}`}
                      className={`w-full text-left flex items-start gap-2 rounded px-2 py-1.5 transition-colors
                        ${on ? "bg-sun-500/15" : "hover:bg-cream-200/60"}`}
                    >
                      <FileText className={`h-4 w-4 mt-0.5 ${on ? "text-sun-700" : "text-ink-900/55"}`} />
                      <div className="flex-1">
                        <div className={`text-xs leading-tight ${on ? "text-ink-900" : "text-ink-900/80"}`}>{d.title}</div>
                        <div className="mono text-[9px] uppercase tracking-[0.18em] text-ink-900/55 mt-0.5">{d.from} · {d.date}</div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="col-span-12 md:col-span-7 border border-ink-900/10 rounded-md bg-cream-50 p-3 min-h-[140px]">
            <div className="flex items-center justify-between">
              <div className="mono text-[9px] uppercase tracking-[0.20em] text-sun-700">/ preview</div>
              <div className="mono text-[9px] uppercase tracking-[0.18em] text-ink-900/55 flex items-center gap-1">
                <Eye className="h-3 w-3" /> Read-only · privileged
              </div>
            </div>
            <div className="serif text-sm text-ink-900 mt-1.5 leading-tight">{activeDoc?.title}</div>
            <div className="mt-2 space-y-1">
              <div className="h-1.5 w-3/4 bg-ink-900/15 rounded" />
              <div className="h-1.5 w-5/6 bg-ink-900/12 rounded" />
              <div className="h-1.5 w-2/3 bg-ink-900/10 rounded" />
              <div className="h-1.5 w-3/5 bg-ink-900/10 rounded" />
            </div>
            <button
              data-testid="matter-open-doc"
              className="mt-3 mono text-[10px] uppercase tracking-[0.22em] text-ink-900/65 hover:text-sun-700 inline-flex items-center gap-1.5 transition-colors"
            >
              Open in viewer <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        </div>

        {/* Quick text to attorney */}
        <div className="mt-4 border-t border-ink-900/10 pt-3">
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700 mb-1.5 flex items-center gap-1.5">
            <MessageSquare className="h-3 w-3" /> quick text to {matter.attorney}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="matter-text-input"
              placeholder={`e.g. "Push v1 to Friday — Acme moved the board meeting."`}
              className="flex-1 rounded-md border border-ink-900/15 bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-900/35 focus:outline-none focus:border-sun-500"
            />
            <button
              onClick={send}
              data-testid="matter-send"
              disabled={!text.trim() || sent}
              className="inline-flex items-center gap-1.5 bg-ink-900 hover:bg-ink-800 disabled:opacity-40 text-cream rounded-md px-3 py-2 mono text-[10px] uppercase tracking-[0.22em] transition-colors"
            >
              {sent ? <Check className="h-3 w-3" /> : <Send className="h-3 w-3" />}
              {sent ? "Delivered" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===================== CompliMind for GCs ===================== */
function CompliMindGCDemo({ prospect }) {
  const slug = (prospect?.slug || "").toLowerCase();
  const isSloan = slug === "sloan";

  const SLOAN_SCRIPTED = {
    "ferguson-msa": {
      summary: "Yes — CompliSun Legal is actively handling the Ferguson MSA renewal. Two artifacts in your matter file:",
      receipts: [
        {
          kind: "meeting", icon: Calendar,
          title: "Working session · Feb 9 · Ferguson MSA renewal scope",
          attendees: "Colin Darke · Jennifer Green · VP Sales (Sloan) · You",
          highlight: 'Concluded: hold pricing on touchless line; carve-in 2% rebate on flushometer SKUs >5,000 units; tighten warranty pass-through to mirror manufacturer-direct terms. Jennifer to redline by Feb 14.',
        },
        {
          kind: "email", icon: Mail,
          title: "Email · Jennifer Green → You · Feb 10",
          subject: "RE: Ferguson rebate language + warranty pass-through",
          highlight: '"On the warranty pass-through: Ferguson\'s draft language puts the manufacturer (Sloan) on the hook for installer error. Pushed back — we hold to OEM-defect scope only. v1 redline coming Friday."',
        },
      ],
    },
    "flushometer-patent": {
      summary: "Yes — CompliSun Legal is enforcing the Sloan 8186 touchless flushometer patent against an importer. Current status:",
      receipts: [
        {
          kind: "meeting", icon: Calendar,
          title: "IP strategy session · Feb 6 · Touchless flushometer enforcement",
          attendees: "Colin Darke · Li-Chen Hung · Outside IP counsel · You",
          highlight: 'Identified accused product imported through Pacific channel. Claim chart maps cleanly on claims 1, 4, 7. Path: C&D first; ITC 337 as escalation if ignored. Jurisdiction strong (importer based in CA).',
        },
        {
          kind: "email", icon: Mail,
          title: "Email · Li-Chen Hung → Importer counsel · Feb 10",
          subject: "Cease & Desist · Sloan 8186 touchless flushometer claims",
          highlight: '"Demand: (1) cessation of importation within 14 days, (2) accounting of units sold, (3) destruction of inventory in US. Reservation of all rights including ITC 337 referral. Response due Feb 24."',
        },
      ],
    },
    "prop-65": {
      summary: "Yes — CompliSun Legal is leading the Prop 65 / NSF 372 lead-free certification refresh for your 2026 line. Status:",
      receipts: [
        {
          kind: "meeting", icon: Calendar,
          title: "Memo · Feb 12 · Lead-free cert scope for 2026 line refresh",
          attendees: "Jennifer Green · VP Engineering (Sloan) · You",
          highlight: 'Two SKUs (royal flushometer + handheld bidet) cross the NSF/ANSI 372 weighted-average lead boundary at the new brass-fitting supplier. Options: (a) reformulate fittings, (b) supplier swap. Decision needed by Mar 1 to keep CA distribution.',
        },
      ],
    },
  };

  const SLOAN_SUGGESTIONS = [
    { key: "ferguson-msa",       text: "Did CompliSun Legal review the Ferguson MSA renewal?" },
    { key: "flushometer-patent", text: "What's the latest on the touchless flushometer patent enforcement?" },
    { key: "prop-65",            text: "Anything outstanding on the Prop 65 / lead-free cert?" },
  ];

  const DEFAULT_SCRIPTED = {
    incentive: {
      summary: "Yes — CompliSun Legal advised on the 2026 incentive plan. Two artifacts in your matter file:",
      receipts: [
        {
          kind: "meeting",
          icon: Calendar,
          title: "Meeting notes · Jan 22 · Incentive plan working session",
          attendees: "Colin Darke · Jennifer Green · Rajesh Damarapati · You",
          highlight: 'Concluded: keep PSU vesting at 3-year cliff; replace TSR modifier with relative EBITDA growth. Jennifer to draft § 162(m) language by Feb 14.',
        },
        {
          kind: "email",
          icon: Mail,
          title: "Email · Jennifer Green → You · Jan 24",
          subject: "RE: 162(m) carve-outs for the 2026 plan",
          highlight: 'On the grandfathering question: any award granted before 11/2/2017 that was binding and unmodified remains deductible. The new PSU grants will NOT qualify; we should plan around that in the proxy disclosure.',
        },
      ],
    },
    msa: {
      summary: "Yes — CompliSun Legal reviewed the Acme MSA. Latest activity:",
      receipts: [
        { kind: "meeting", icon: Calendar, title: "Call notes · Feb 5 · Acme MSA pre-redline", attendees: "Li-Chen Hung · Acme General Counsel · You",
          highlight: "Agreed: tighten IP assignment to work-product only; mutual indemnity capped at 12 months of fees; SLA credits capped at 25%." },
        { kind: "email", icon: Mail, title: "Email · Li-Chen → Acme counsel · Feb 10", subject: "RE: MSA v3 — countersign", highlight: "Sent v3 with our changes accepted on IP + indemnity. Awaiting their countersign; soft chase scheduled for Feb 17 if no response." },
      ],
    },
    dpa: {
      summary: "Yes — CompliSun Legal flagged the Snowflake DPA renewal. Current status:",
      receipts: [
        { kind: "meeting", icon: Calendar, title: "Memo · Feb 12 · Snowflake DPA renewal kickoff", attendees: "Jennifer Green · You",
          highlight: "Renewal lands April 1. Risk: revised sub-processor list adds two non-adequacy-country processors. Need SCCs + supplementary measures before signature." },
      ],
    },
  };

  const DEFAULT_SUGGESTIONS = [
    { key: "incentive", text: "Did CompliSun Legal provide advice on an incentive plan?" },
    { key: "msa",       text: "What's the latest on the Acme MSA?" },
    { key: "dpa",       text: "Anything outstanding on the Snowflake DPA?" },
  ];

  const SCRIPTED    = isSloan ? SLOAN_SCRIPTED    : DEFAULT_SCRIPTED;
  const SUGGESTIONS = isSloan ? SLOAN_SUGGESTIONS : DEFAULT_SUGGESTIONS;

  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState(false);
  const inputRef = useRef(null);

  const ask = (key) => {
    const q = SUGGESTIONS.find((s) => s.key === key);
    if (!q || pending) return;
    setMessages((m) => [...m, { role: "user", text: q.text }]);
    setPending(true);
    setTimeout(() => {
      setMessages((m) => [...m, { role: "ai", payload: SCRIPTED[key] }]);
      setPending(false);
    }, 700);
  };

  return (
    <div className="grid grid-cols-12 gap-4" data-testid="complimind-gc-demo">
      <div className="col-span-12 md:col-span-8 border border-ink-900/10 rounded-md bg-white/80 p-4 min-h-[360px] flex flex-col">
        <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700 flex items-center gap-1.5">
          <Brain className="h-3 w-3" /> CompliMind · trained on your matter file
        </div>

        <div className="flex-1 mt-3 space-y-3 overflow-auto pr-1" data-testid="complimind-thread">
          {messages.length === 0 && (
            <div className="text-sm text-ink-900/55 italic">
              Ask CompliMind anything about your legal history with us — it cites the meeting notes, emails, and docs.
            </div>
          )}
          {messages.map((m, i) => (
            <div key={`${m.role}-${i}-${(m.text || m.payload?.summary || "").slice(0, 24)}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role === "user" ? (
                <div className="bg-ink-900 text-cream rounded-md rounded-br-sm px-3 py-2 text-sm max-w-[80%]">{m.text}</div>
              ) : (
                <div className="bg-cream-100 border border-sun-500/30 rounded-md rounded-bl-sm px-3 py-3 text-sm max-w-[90%] space-y-2.5">
                  <div className="text-ink-900">{m.payload.summary}</div>
                  {m.payload.receipts.map((r) => {
                    const Ic = r.icon;
                    return (
                      <div key={r.title} className="border border-ink-900/10 rounded p-2.5 bg-white/80">
                        <div className="flex items-center gap-1.5 mono text-[9px] uppercase tracking-[0.20em] text-sun-700">
                          <Ic className="h-3 w-3" /> {r.kind}
                        </div>
                        <div className="serif text-sm text-ink-900 mt-1 leading-tight">{r.title}</div>
                        {r.attendees && <div className="text-[11px] text-ink-900/65 mt-0.5">Attendees: {r.attendees}</div>}
                        {r.subject && <div className="text-[11px] text-ink-900/65 mt-0.5">Subject: {r.subject}</div>}
                        <div className="text-xs text-ink-900/80 mt-1.5 leading-relaxed italic">"{r.highlight}"</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
          {pending && (
            <div className="flex justify-start">
              <div className="bg-cream-100 border border-sun-500/30 rounded-md px-3 py-2 text-sm text-ink-900/60 flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-sun-700" /> Surfacing meeting notes + emails…
              </div>
            </div>
          )}
        </div>

        <div className="mt-3 border-t border-ink-900/10 pt-3 flex gap-2">
          <input
            ref={inputRef}
            type="text"
            placeholder="Type a question · or pick one →"
            className="flex-1 rounded-md border border-ink-900/15 bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-900/35 focus:outline-none focus:border-sun-500"
            data-testid="complimind-input"
            readOnly
            title="In the live build this is a real chat input — for the demo, use the suggestions →"
          />
          <button
            disabled
            className="inline-flex items-center gap-1.5 bg-ink-900/50 text-cream rounded-md px-3 py-2 mono text-[10px] uppercase tracking-[0.22em]"
          >
            <Send className="h-3 w-3" /> Ask
          </button>
        </div>
      </div>

      <div className="col-span-12 md:col-span-4">
        <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-2 flex items-center gap-1.5">
          <ArrowDown className="h-3 w-3" /> try a question
        </div>
        <div className="space-y-2">
          {SUGGESTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => ask(s.key)}
              disabled={pending}
              data-testid={`complimind-suggest-${s.key}`}
              className="w-full text-left rounded-md border border-ink-900/12 bg-white/60 hover:border-sun-500 hover:bg-sun-500/10 px-3 py-2.5 text-sm text-ink-900/85 transition-colors disabled:opacity-50"
            >
              {s.text}
            </button>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-ink-900/55 leading-relaxed">
          CompliMind only answers from <strong className="text-ink-900">your</strong> matter file —
          your meeting notes, your emails, your drafts. No public-internet hallucinations, no leakage.
        </p>
      </div>
    </div>
  );
}

/* ===================== Hour Tracker · Client Markers ===================== */
function HourTrackerDemo({ prospect }) {
  const slug = (prospect?.slug || "").toLowerCase();
  const isSloan = slug === "sloan";

  // Default matter — generic GC example.
  const DEFAULT_MATTER = {
    name: "2026 Executive Incentive Plan — Annual Refresh",
    budgeted: 24,
    spent: 14.5,
    attorneys: ["Jennifer Green", "Colin Darke"],
  };
  const DEFAULT_MARKERS = [
    { hours: 8,  action: "stop",   note: "Pause and reach out before continuing." },
    { hours: 16, action: "notify", note: "Quick status check-in — no stop." },
    { hours: 24, action: "stop",   note: "Mandatory client review before next billable hour." },
  ];

  // Sloan — buy-side diligence on a tuck-in acquisition. Realistic burn for a manufacturer in growth mode.
  const SLOAN_MATTER = {
    name: "Project Crescent — buy-side acquisition diligence",
    budgeted: 30,
    spent: 18.5,
    attorneys: ["Jennifer Green", "Outside M&A counsel", "Colin Darke"],
  };
  const SLOAN_MARKERS = [
    { hours: 12, action: "notify", note: "Status note · Reg-flag triage from data-room review." },
    { hours: 20, action: "stop",   note: "Pause before exclusivity expires — sync with CFO + Board liaison." },
    { hours: 30, action: "stop",   note: "Cap reached. No further hours without written GC approval." },
  ];

  const MATTER = isSloan ? SLOAN_MATTER : DEFAULT_MATTER;
  const DEFAULTS = isSloan ? SLOAN_MARKERS : DEFAULT_MARKERS;

  const [markers, setMarkers] = useState(DEFAULTS);
  const [draftH, setDraftH] = useState("");
  const [draftAction, setDraftAction] = useState("stop");
  const [draftNote, setDraftNote] = useState("");

  const pct = Math.min(100, Math.round((MATTER.spent / MATTER.budgeted) * 100));
  const nextTrigger = [...markers].sort((a, b) => a.hours - b.hours).find((m) => m.hours > MATTER.spent);

  const addMarker = () => {
    const h = parseFloat(draftH);
    if (!h || h <= 0 || !draftNote.trim()) {
      toast.error("Add hours threshold and a note.");
      return;
    }
    setMarkers((ms) => [...ms, { hours: h, action: draftAction, note: draftNote.trim() }].sort((a, b) => a.hours - b.hours));
    setDraftH(""); setDraftNote(""); setDraftAction("stop");
    toast.success(`Marker at ${h}h added. We'll ${draftAction === "stop" ? "stop" : "notify"} when we hit it.`);
  };
  const removeMarker = (i) => setMarkers((ms) => ms.filter((_, idx) => idx !== i));

  return (
    <div className="grid grid-cols-12 gap-4" data-testid="hours-demo">
      {/* Left: matter + budget bar */}
      <div className="col-span-12 md:col-span-7 border border-ink-900/10 rounded-md bg-white/80 p-4">
        <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700">/ matter</div>
        <div className="serif text-lg text-ink-900 leading-tight mt-1">{MATTER.name}</div>
        <div className="mono text-[10px] uppercase tracking-[0.18em] text-ink-900/55 mt-0.5">{MATTER.attorneys.join(" · ")}</div>

        {/* Budget bar with markers overlaid */}
        <div className="mt-5">
          <div className="flex items-baseline justify-between mb-1.5">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55">/ hours used</div>
            <div className="serif text-base text-ink-900">{MATTER.spent} <span className="text-ink-900/45 text-sm">/ {MATTER.budgeted}h budget</span></div>
          </div>
          <div className="relative h-7 rounded-full bg-ink-900/8 overflow-visible" data-testid="hours-bar">
            <div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-sun-500 to-ember-500 rounded-full"
              style={{ width: `${pct}%` }}
            />
            {markers.map((m) => {
              const left = Math.min(100, (m.hours / MATTER.budgeted) * 100);
              const stop = m.action === "stop";
              return (
                <div
                  key={m.note}
                  className="absolute top-1/2 -translate-y-1/2"
                  style={{ left: `${left}%` }}
                  title={`${m.hours}h · ${m.note}`}
                >
                  <div className={`-translate-x-1/2 h-9 w-0.5 ${stop ? "bg-ember-700" : "bg-sun-700"}`} />
                  <div className={`-translate-x-1/2 absolute top-9 mt-1 px-1.5 py-0.5 rounded text-[9px] mono uppercase tracking-[0.18em] whitespace-nowrap
                    ${stop ? "bg-ember-500 text-cream" : "bg-sun-500 text-ink-900"}`}>
                    {m.hours}h · {stop ? "stop" : "notify"}
                  </div>
                </div>
              );
            })}
          </div>

          {nextTrigger && (
            <div className="mt-12 flex items-start gap-2 text-xs text-ink-900/75">
              <Bell className="h-3.5 w-3.5 text-ember-700 mt-0.5 flex-shrink-0" />
              <span>
                Next trigger at <strong className="text-ink-900">{nextTrigger.hours}h</strong>{" "}
                ({(nextTrigger.hours - MATTER.spent).toFixed(1)}h to go) ·{" "}
                {nextTrigger.action === "stop"
                  ? "we'll pause work and reach out before continuing."
                  : "we'll send you a status note — no pause."}
              </span>
            </div>
          )}
        </div>

        {/* Markers list */}
        <div className="mt-6">
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-2">/ your guardrails</div>
          <ul className="space-y-1.5" data-testid="hours-markers">
            {markers.map((m, i) => {
              const stop = m.action === "stop";
              return (
                <li key={`${m.hours}-${m.note}`} className="flex items-start gap-2.5 border border-ink-900/8 rounded px-2.5 py-2 bg-cream-50">
                  <div className={`h-7 w-7 rounded-full grid place-items-center flex-shrink-0
                    ${stop ? "bg-ember-500/15" : "bg-sun-500/15"}`}>
                    {stop ? <Clock className="h-3.5 w-3.5 text-ember-700" /> : <Bell className="h-3.5 w-3.5 text-sun-700" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-ink-900 leading-tight"><strong>{m.hours}h ·</strong> {m.note}</div>
                    <div className="mono text-[9px] uppercase tracking-[0.20em] mt-0.5 text-ink-900/55">
                      {stop ? "Stop & reach out" : "Notify only"}
                    </div>
                  </div>
                  <button
                    onClick={() => removeMarker(i)}
                    data-testid={`hours-remove-${i}`}
                    className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/45 hover:text-ember-700 transition-colors"
                  >
                    Remove
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Right: add a marker */}
      <div className="col-span-12 md:col-span-5 border border-ink-900/10 rounded-md bg-white/70 p-4">
        <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700">/ add a guardrail</div>
        <div className="serif text-lg text-ink-900 leading-tight mt-1">Tell us when to pause.</div>
        <p className="text-xs text-ink-900/65 mt-1 leading-relaxed">
          You control the burn rate — set a marker at any hour threshold. We'll either pause
          the work or fire a status note, depending on the action you pick.
        </p>

        <div className="mt-4 space-y-3">
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55">At how many hours?</label>
            <input
              type="number"
              min="0.5"
              step="0.5"
              value={draftH}
              onChange={(e) => setDraftH(e.target.value)}
              data-testid="hours-input-h"
              placeholder="e.g. 20"
              className="mt-1 w-full rounded-md border border-ink-900/15 bg-white px-3 py-2 text-sm text-ink-900 focus:outline-none focus:border-sun-500"
            />
          </div>
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-1">Action</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setDraftAction("stop")}
                data-testid="hours-action-stop"
                className={`text-left rounded-md border px-3 py-2 transition-colors
                  ${draftAction === "stop" ? "border-ember-500 bg-ember-500/10" : "border-ink-900/12 bg-white/60 hover:border-ink-900/25"}`}
              >
                <div className="serif text-sm text-ink-900 leading-tight">Stop & reach out</div>
                <div className="mono text-[9px] uppercase tracking-[0.18em] text-ink-900/55 mt-0.5">Pause until you OK more hours.</div>
              </button>
              <button
                onClick={() => setDraftAction("notify")}
                data-testid="hours-action-notify"
                className={`text-left rounded-md border px-3 py-2 transition-colors
                  ${draftAction === "notify" ? "border-sun-500 bg-sun-500/10" : "border-ink-900/12 bg-white/60 hover:border-ink-900/25"}`}
              >
                <div className="serif text-sm text-ink-900 leading-tight">Notify only</div>
                <div className="mono text-[9px] uppercase tracking-[0.18em] text-ink-900/55 mt-0.5">Status note · keep working.</div>
              </button>
            </div>
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55">Note for our team</label>
            <textarea
              value={draftNote}
              onChange={(e) => setDraftNote(e.target.value)}
              data-testid="hours-input-note"
              rows={2}
              placeholder='e.g. "Reach out before exceeding board-approved cap."'
              className="mt-1 w-full rounded-md border border-ink-900/15 bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-900/35 focus:outline-none focus:border-sun-500"
            />
          </div>
          <button
            onClick={addMarker}
            data-testid="hours-add"
            className="w-full inline-flex items-center justify-center gap-2 bg-ink-900 hover:bg-ink-800 text-cream rounded-md px-4 py-2.5 mono text-[10px] uppercase tracking-[0.22em] transition-colors"
          >
            Add marker <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}


/* ===================== Partner Monitoring (sponsor-bank cross-tenant view) ===================== */
function PartnerMonitoringDemo({ prospect }) {
  const slug = (prospect?.slug || "").toLowerCase();
  // Tailored partner roster per sponsor bank. Public names that bank channel readers will recognize.
  const PARTNERS_BY_SLUG = {
    "cross-river-bank": [
      { id: "best-egg",   name: "Best Egg",      type: "Consumer lending",   tenantId: "CRB-TEN-0142", since: "2019",
        kpis: { ofac: "clean", complaints: "+8%", aml: "in-band", attestation: "current" },
        active: 3, last: "12 min ago" },
      { id: "kikoff",     name: "Kikoff",        type: "Credit-builder",     tenantId: "CRB-TEN-0218", since: "2021",
        kpis: { ofac: "clean", complaints: "+34%", aml: "in-band", attestation: "current" },
        active: 1, last: "6 hr ago", status: "warn",
        flagged: "Complaint volume drift exceeds quarterly band (+34% MoM)." },
      { id: "claritypay", name: "ClarityPay",    type: "BNPL / payments",    tenantId: "CRB-TEN-0301", since: "2023",
        kpis: { ofac: "clean", complaints: "−3%", aml: "in-band", attestation: "due 14d" },
        active: 0, last: "1 day ago" },
      { id: "alleviate",  name: "Alleviate",     type: "Debt resolution",    tenantId: "CRB-TEN-0186", since: "2020",
        kpis: { ofac: "clean", complaints: "+11%", aml: "review", attestation: "current" },
        active: 2, last: "47 min ago", status: "warn",
        flagged: "2 transactions tripped velocity rule · pending analyst review." },
      { id: "greenbox",   name: "Greenbox Capital", type: "Small-biz lending", tenantId: "CRB-TEN-0247", since: "2022",
        kpis: { ofac: "clean", complaints: "−6%", aml: "in-band", attestation: "current" },
        active: 0, last: "3 hr ago" },
      { id: "earnin",     name: "Earnin",        type: "Earned-wage access", tenantId: "CRB-TEN-0163", since: "2019",
        kpis: { ofac: "clean", complaints: "+4%", aml: "in-band", attestation: "current" },
        active: 1, last: "22 min ago" },
    ],
    "tab-bank": [
      { id: "trucker-pay", name: "Trucker Pay",   type: "Driver payroll",    tenantId: "TAB-TEN-0021", since: "2022",
        kpis: { ofac: "clean", complaints: "+5%", aml: "in-band", attestation: "current" },
        active: 1, last: "18 min ago" },
      { id: "fleetcard",   name: "FleetCard",     type: "Fuel + spend card", tenantId: "TAB-TEN-0034", since: "2023",
        kpis: { ofac: "clean", complaints: "+22%", aml: "review", attestation: "due 7d" },
        active: 2, last: "1 hr ago", status: "warn",
        flagged: "Aged attestation + AML pattern review queued — coordinate before exam window." },
      { id: "sba-flow",    name: "SBA-Flow",      type: "SBA loan origination", tenantId: "TAB-TEN-0019", since: "2021",
        kpis: { ofac: "clean", complaints: "−2%", aml: "in-band", attestation: "current" },
        active: 0, last: "4 hr ago" },
      { id: "haulpay",     name: "HaulPay",       type: "Freight factoring", tenantId: "TAB-TEN-0027", since: "2022",
        kpis: { ofac: "clean", complaints: "+9%", aml: "in-band", attestation: "current" },
        active: 1, last: "33 min ago" },
    ],
    "celtic-bank": [
      { id: "kabbage",   name: "Square Cap-style partner", type: "SMB lending", tenantId: "CEL-TEN-0007", since: "2018",
        kpis: { ofac: "clean", complaints: "+6%", aml: "in-band", attestation: "current" },
        active: 1, last: "14 min ago" },
      { id: "marlette",  name: "Marlette-style partner",   type: "Consumer lending", tenantId: "CEL-TEN-0011", since: "2020",
        kpis: { ofac: "clean", complaints: "+12%", aml: "in-band", attestation: "due 10d" },
        active: 0, last: "2 hr ago" },
      { id: "lendingpoint", name: "LendingPoint-style partner", type: "POS lending", tenantId: "CEL-TEN-0014", since: "2021",
        kpis: { ofac: "clean", complaints: "+28%", aml: "review", attestation: "current" },
        active: 2, last: "55 min ago", status: "warn",
        flagged: "Sustained complaint drift + AML pattern review — schedule program-team sync." },
    ],
  };

  const DEFAULT_PARTNERS = PARTNERS_BY_SLUG["cross-river-bank"];
  const PARTNERS = PARTNERS_BY_SLUG[slug] || DEFAULT_PARTNERS;
  const bankLabel = prospect?.company_name || "Sponsor Bank";

  const [activeId, setActiveId] = useState(PARTNERS[0].id);
  const active = PARTNERS.find((p) => p.id === activeId) || PARTNERS[0];

  const totalActive = PARTNERS.reduce((n, p) => n + (p.active || 0), 0);
  const warnCount = PARTNERS.filter((p) => p.status === "warn").length;

  const statusDot = (p) => {
    if (p.status === "warn") return "bg-amber-500";
    if (p.status === "stop") return "bg-rose-500";
    return "bg-emerald-500";
  };

  const kpiTone = (label, val) => {
    const v = String(val || "").toLowerCase();
    if (v === "clean" || v === "current" || v === "in-band") return "text-emerald-700 bg-emerald-500/10 border-emerald-500/25";
    if (v.includes("review") || v.includes("due")) return "text-amber-700 bg-amber-500/15 border-amber-500/30";
    if (v.startsWith("+") && parseInt(v.replace(/[^0-9-]/g, ""), 10) >= 20) return "text-amber-700 bg-amber-500/15 border-amber-500/30";
    if (v.startsWith("-") || v.startsWith("−")) return "text-emerald-700 bg-emerald-500/10 border-emerald-500/25";
    return "text-ink-900/80 bg-cream-100 border-ink-900/10";
  };

  // Mock exception log
  const exceptionsByPartner = {
    "best-egg":   [
      { when: "10 min ago",  what: "Quarterly partner-risk assessment auto-generated · sent to CRB Program Manager", tone: "info" },
      { when: "2 hr ago",    what: "OFAC weekly re-screen completed · 0 hits across 142k accounts",                tone: "ok" },
    ],
    "kikoff":     [
      { when: "47 min ago",  what: "Complaint volume drift +34% MoM (band ±15%) · escalation drafted",             tone: "warn" },
      { when: "6 hr ago",    what: "Marketing review queued · 2 promo emails flagged for Reg-Z disclosure",        tone: "warn" },
      { when: "yesterday",   what: "Training attestation cycle closed · 96% completion across partner team",       tone: "ok" },
    ],
    "claritypay": [
      { when: "1 hr ago",    what: "Quarterly control-attestation reminder dispatched · due in 14 days",           tone: "info" },
      { when: "2 days ago",  what: "Vendor stack delta detected · 1 new sub-processor (Datadog) — auto-DPA queued", tone: "info" },
    ],
    "alleviate":  [
      { when: "47 min ago",  what: "2 transactions tripped velocity rule · analyst review queued",                 tone: "warn" },
      { when: "5 hr ago",    what: "Settlement-letter template change detected · marketing review opened",         tone: "warn" },
    ],
    "greenbox":   [
      { when: "3 hr ago",    what: "OFAC weekly re-screen completed · 0 hits",                                     tone: "ok" },
      { when: "yesterday",   what: "Quarterly risk assessment delivered to CRB Program Manager",                   tone: "ok" },
    ],
    "earnin":     [
      { when: "22 min ago",  what: "Daily transaction-pattern check · all in-band",                                tone: "ok" },
      { when: "yesterday",   what: "Sponsor-bank attestation pack refreshed for the quarter",                       tone: "ok" },
    ],
    "trucker-pay": [
      { when: "18 min ago",  what: "Driver-payout settlement reconciliation · 0 exceptions",                       tone: "ok" },
      { when: "yesterday",   what: "Quarterly risk-assessment pack delivered",                                     tone: "ok" },
    ],
    "fleetcard": [
      { when: "1 hr ago",    what: "Attestation due in 7 days · auto-reminder sent to partner CCO",                tone: "warn" },
      { when: "3 hr ago",    what: "AML pattern review · 1 merchant category trending — analyst assigned",         tone: "warn" },
    ],
    "sba-flow":   [
      { when: "4 hr ago",    what: "SBA-form generator run · 22 packages produced, 0 exceptions",                  tone: "ok" },
      { when: "yesterday",   what: "Quarterly assessment delivered",                                                tone: "ok" },
    ],
    "haulpay":    [
      { when: "33 min ago",  what: "Factoring-vendor stack delta detected · new processor — DPA queued",           tone: "info" },
      { when: "yesterday",   what: "OFAC weekly re-screen · clean",                                                tone: "ok" },
    ],
    "kabbage":    [{ when: "14 min ago", what: "Daily KPI roll-up delivered", tone: "ok" }],
    "marlette":   [{ when: "2 hr ago", what: "Attestation cycle opens in 10 days", tone: "info" }],
    "lendingpoint": [
      { when: "55 min ago", what: "Complaint drift +28% MoM · escalation drafted", tone: "warn" },
      { when: "5 hr ago",   what: "AML pattern review queued — analyst assigned",  tone: "warn" },
    ],
  };
  const events = exceptionsByPartner[active.id] || [
    { when: "—", what: "No recent events", tone: "info" },
  ];

  const toneCls = (t) => {
    if (t === "warn") return "bg-amber-500/15 text-amber-700 border-amber-500/30";
    if (t === "ok") return "bg-emerald-500/10 text-emerald-700 border-emerald-500/30";
    return "bg-ink-900/5 text-ink-900/70 border-ink-900/10";
  };

  return (
    <div className="grid grid-cols-12 gap-4" data-testid="partner-monitoring-demo">
      {/* Header strip with cross-tenant counters */}
      <div className="col-span-12 flex flex-wrap items-center justify-between gap-3 border border-ink-900/10 rounded-md bg-white/80 px-4 py-3"
           data-testid="partner-monitor-header">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-md grid place-items-center bg-sun-500/15">
            <Network className="h-4 w-4 text-sun-700" strokeWidth={1.6} />
          </div>
          <div className="min-w-0">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700">/ {bankLabel} · oversight view</div>
            <div className="serif text-base text-ink-900 leading-tight truncate">
              {PARTNERS.length} fintech partner tenants · cross-tenant read-only
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mono text-[10px] uppercase tracking-[0.22em]">
          <span className="inline-flex items-center gap-1.5 border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 rounded-full px-2.5 py-1">
            <Activity className="h-3 w-3" /> {PARTNERS.length - warnCount} healthy
          </span>
          <span className="inline-flex items-center gap-1.5 border border-amber-500/40 bg-amber-500/15 text-amber-700 rounded-full px-2.5 py-1">
            <ShieldAlert className="h-3 w-3" /> {warnCount} need attention
          </span>
          <span className="inline-flex items-center gap-1.5 border border-ink-900/15 bg-cream-100 text-ink-900/75 rounded-full px-2.5 py-1">
            <Radio className="h-3 w-3" /> {totalActive} open exceptions
          </span>
        </div>
      </div>

      {/* Left: partner roster */}
      <div className="col-span-12 md:col-span-5">
        <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-2">
          / partner tenants · click any row
        </div>
        <ul className="space-y-2" data-testid="partner-monitor-list">
          {PARTNERS.map((p) => {
            const on = p.id === activeId;
            return (
              <li key={p.id}>
                <button
                  onClick={() => setActiveId(p.id)}
                  data-testid={`partner-row-${p.id}`}
                  className={`w-full text-left rounded-md border px-3 py-2.5 transition-colors
                    ${on ? "border-sun-500 bg-sun-500/10" : "border-ink-900/12 bg-white/70 hover:border-ink-900/25"}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${statusDot(p)}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="serif text-base text-ink-900 leading-tight">{p.name}</span>
                        <span className="mono text-[9px] uppercase tracking-[0.20em] text-ink-900/55">{p.tenantId}</span>
                      </div>
                      <div className="mono text-[9px] uppercase tracking-[0.18em] text-ink-900/60 mt-0.5">
                        {p.type} · partner since {p.since} · last signal {p.last}
                      </div>
                      {p.flagged && (
                        <div className="mt-1.5 inline-flex items-start gap-1.5 text-[11px] text-amber-700 leading-snug">
                          <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" /> {p.flagged}
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className={`mono text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded
                        ${p.active > 0 ? "bg-amber-500/15 text-amber-700" : "bg-emerald-500/10 text-emerald-700"}`}>
                        {p.active} open
                      </div>
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
        <p className="mt-3 text-[11px] text-ink-900/60 leading-relaxed">
          You see <strong className="text-ink-900">every</strong> partner. Partners only see their own
          tenant — by design. Same methodology applied across the book; same evidence cadence.
        </p>
      </div>

      {/* Right: partner detail */}
      <div className="col-span-12 md:col-span-7 border border-ink-900/10 rounded-md bg-white/80 p-4" data-testid="partner-monitor-detail">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700">/ tenant · {active.tenantId}</div>
            <div className="serif text-xl text-ink-900 leading-tight mt-0.5">{active.name}</div>
            <div className="mono text-[10px] uppercase tracking-[0.18em] text-ink-900/55 mt-0.5">
              {active.type} · sponsor since {active.since}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-[0.22em] rounded-full px-2.5 py-1 border
              ${active.status === "warn"
                ? "border-amber-500/40 bg-amber-500/15 text-amber-700"
                : "border-emerald-500/30 bg-emerald-500/10 text-emerald-700"}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${statusDot(active)}`} />
              {active.status === "warn" ? "Attention needed" : "Healthy"}
            </span>
          </div>
        </div>

        {/* KPIs */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="partner-monitor-kpis">
          {[
            { label: "OFAC re-screen",      val: active.kpis.ofac,        icon: ShieldCheck },
            { label: "Complaint drift · MoM", val: active.kpis.complaints,  icon: TrendingUp },
            { label: "AML pattern check",   val: active.kpis.aml,         icon: Activity },
            { label: "Control attestation", val: active.kpis.attestation, icon: ShieldAlert },
          ].map((k) => {
            const Ic = k.icon;
            return (
              <div key={k.label} className={`rounded-md border px-2.5 py-2 ${kpiTone(k.label, k.val)}`}>
                <div className="flex items-center gap-1.5 mono text-[9px] uppercase tracking-[0.20em] opacity-80">
                  <Ic className="h-3 w-3" /> {k.label}
                </div>
                <div className="serif text-base mt-0.5 leading-tight">{k.val}</div>
              </div>
            );
          })}
        </div>

        {/* Event log */}
        <div className="mt-4">
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-1.5">/ recent signals · automated</div>
          <ul className="space-y-1.5">
            {events.map((e) => (
              <li key={e.what} className={`text-xs border rounded px-2.5 py-1.5 flex items-start gap-2 ${toneCls(e.tone)}`}>
                <span className="mono text-[9px] uppercase tracking-[0.20em] opacity-80 min-w-[80px]">{e.when}</span>
                <span className="leading-relaxed">{e.what}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Actions */}
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            data-testid="partner-monitor-evidence"
            onClick={() => toast.success(`Evidence pack queued for ${active.name} · delivered to your team folder.`)}
            className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-[0.22em] border border-ink-900/15 bg-white rounded px-3 py-2 hover:border-sun-500 hover:text-sun-700 transition-colors"
          >
            <Download className="h-3 w-3" /> Open evidence pack
          </button>
          <button
            data-testid="partner-monitor-report"
            onClick={() => toast.success(`Exception report sent to ${bankLabel} oversight team.`)}
            className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-[0.22em] border border-ink-900/15 bg-white rounded px-3 py-2 hover:border-sun-500 hover:text-sun-700 transition-colors"
          >
            <FileText className="h-3 w-3" /> Export exception report
          </button>
          <button
            data-testid="partner-monitor-notify"
            onClick={() => toast.success(`${active.name} CCO notified · response SLA started.`)}
            className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-[0.22em] bg-ink-900 hover:bg-ink-800 text-cream rounded px-3 py-2 transition-colors"
          >
            <Send className="h-3 w-3" /> Notify partner CCO
          </button>
          <button
            data-testid="partner-monitor-open-tenant"
            onClick={() => toast.success(`Opening ${active.name} tenant in read-only mode…`)}
            className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-[0.22em] border border-ink-900/15 bg-white rounded px-3 py-2 hover:border-sun-500 hover:text-sun-700 transition-colors"
          >
            <ExternalLink className="h-3 w-3" /> Open tenant (read-only)
          </button>
        </div>
      </div>
    </div>
  );
}
